import { Box, Heading, List, Paragraph, Text } from '@zillow/constellation-10';
import { useFormContext } from 'react-hook-form';
import { compact, isEmpty, uniq } from 'lodash-es';
import { pluralize } from '@zg-rentals/util';
import { ApplicationConfigurationCustomQuestion } from '@zg-rentals/ts-capps-client';
import type { Answers, Residence } from '../../../../models/rental-application';
import { useIsCustomQuestionEnabled } from '../../../../hooks/useIsCustomQuestionEnabled';
import { getPhoneNumberScreenFormat, isServerDate } from '../../../../utils/formatters';
import { STEP_IDS } from '../../../../constants/wizard';
import { ContactPermissionCard, useListingOwnerDisplayName } from './ContactPermissionCard';
import { EditInStepLink } from './EditInStepLink';
import type { SupplementalSectionProps } from './supplementalSections';

type LandlordContact = {
  name: string;
  email?: string | null;
  phone?: string | null;
  period: string | null;
};

function getYear(date?: string | null) {
  return date && isServerDate(date) ? date.slice(0, 4) : null;
}

function getResidencePeriod(residence: Residence, isCurrent: boolean) {
  if (isCurrent) {
    return 'current';
  }
  const years = compact([getYear(residence.startDate), getYear(residence.endDate)]);
  return isEmpty(years) ? null : uniq(years).join('–');
}

function toLandlordContact(residence: Residence | null | undefined, isCurrent: boolean): Array<LandlordContact> {
  const name = residence?.manager?.name?.trim();
  return residence && name
    ? [
        {
          name,
          email: residence.manager?.email,
          phone: residence.manager?.phone,
          period: getResidencePeriod(residence, isCurrent),
        },
      ]
    : [];
}

function formatLandlordContact(
  landlord: LandlordContact,
  { isPhoneEnabled, isEmailEnabled }: { isPhoneEnabled: boolean; isEmailEnabled: boolean },
) {
  return compact([
    landlord.name,
    isPhoneEnabled ? getPhoneNumberScreenFormat(landlord.phone) : null,
    isEmailEnabled ? landlord.email : null,
    landlord.period,
  ]).join(' · ');
}

function getLandlordContacts(
  currentResidence: Answers['currentResidence'],
  pastResidences: Answers['pastResidences'],
): Array<LandlordContact> {
  return [
    ...toLandlordContact(currentResidence, true),
    ...(pastResidences ?? []).flatMap((residence) => toLandlordContact(residence, false)),
  ];
}

export function hasLandlordContact(answers: Answers) {
  return !isEmpty(getLandlordContacts(answers.currentResidence, answers.pastResidences));
}

function useLandlordContactSettings() {
  return {
    isPhoneEnabled: useIsCustomQuestionEnabled(ApplicationConfigurationCustomQuestion.LANDLORD_PHONE),
    isEmailEnabled: useIsCustomQuestionEnabled(ApplicationConfigurationCustomQuestion.LANDLORD_EMAIL),
    isPermissionEnabled: useIsCustomQuestionEnabled(ApplicationConfigurationCustomQuestion.LANDLORD_CONTACT_PERMISSION),
  };
}

function LandlordContactList({
  landlords,
  isPhoneEnabled,
  isEmailEnabled,
}: {
  landlords: Array<LandlordContact>;
  isPhoneEnabled: boolean;
  isEmailEnabled: boolean;
}) {
  return (
    <List.Root css={{ marginBlock: 'none' }}>
      {landlords.map((landlord, index) => (
        <List.Item key={index} css={{ paddingBlock: 'none' }}>
          {formatLandlordContact(landlord, { isPhoneEnabled, isEmailEnabled })}
        </List.Item>
      ))}
    </List.Root>
  );
}

export function LandlordContactSection({ applicationId, onPersist }: SupplementalSectionProps) {
  const { watch } = useFormContext();
  const { isPhoneEnabled, isEmailEnabled, isPermissionEnabled } = useLandlordContactSettings();
  const listingOwnerName = useListingOwnerDisplayName();
  const landlords = getLandlordContacts(watch('currentResidence'), watch('pastResidences'));
  const landlordNoun = pluralize('landlord', landlords.length);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      {(isPhoneEnabled || isEmailEnabled) && (
        <Box>
          <Heading level={4} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
            Landlord {pluralize('contact', landlords.length)}
          </Heading>
          <Paragraph>Your {landlordNoun} from your residence history:</Paragraph>
          <LandlordContactList landlords={landlords} isPhoneEnabled={isPhoneEnabled} isEmailEnabled={isEmailEnabled} />
          <Paragraph>
            <EditInStepLink
              applicationId={applicationId}
              stepId={STEP_IDS.CURRENT_RESIDENCE}
              label="Residence"
              onPersist={onPersist}
            />
          </Paragraph>
        </Box>
      )}
      {isPermissionEnabled && (
        <ContactPermissionCard
          name="canContactLandlords"
          title={`Let ${listingOwnerName} connect with your ${landlordNoun}`}
          description={`Your ${landlordNoun} may be contacted to verify your rental history and tenancy.`}
        />
      )}
    </Box>
  );
}

export function LandlordContactOverview({ answers }: SupplementalSectionProps) {
  const { isPhoneEnabled, isEmailEnabled, isPermissionEnabled } = useLandlordContactSettings();
  const landlords = getLandlordContacts(answers.currentResidence, answers.pastResidences);
  const landlordNoun = pluralize('landlord', landlords.length);

  return (
    <>
      {(isPhoneEnabled || isEmailEnabled) && (
        <>
          <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
            Landlord {pluralize('contact', landlords.length)}
          </Heading>
          {landlords.map((landlord, index) => (
            <Paragraph key={index}>{formatLandlordContact(landlord, { isPhoneEnabled, isEmailEnabled })}</Paragraph>
          ))}
        </>
      )}
      {isPermissionEnabled && answers.canContactLandlords != null && (
        <>
          <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
            Contact {landlordNoun}?
          </Heading>
          <Text>{answers.canContactLandlords ? 'Yes' : 'No'}</Text>
        </>
      )}
    </>
  );
}
